import {useState, useEffect, useCallback} from "react";
import AuthService, {UserProfile, UserRole} from "../services/firebase/auth";

export interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  error: string | null;
  isLoggedIn: boolean;
  roles: UserRole[];

  // Authentication methods
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (accountType?: "player" | "dm") => Promise<void>;
  signUp: (
    email: string,
    password: string,
    displayName: string,
    accountType: "player" | "dm"
  ) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;

  // Email verification methods
  checkEmailVerification: () => Promise<boolean>;
  resendEmailVerification: () => Promise<void>;
  refreshVerificationStatus: () => Promise<boolean>;

  // User profile methods
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  updateLocalUserState: (updates: Partial<UserProfile>) => void;
  updateSettings: (updates: {
    displayName?: string;
    bio?: string;
    preferences?: {
      emailNotifications: {
        gameReminders: boolean;
        leagueUpdates: boolean;
      };
    };
  }) => Promise<void>;
  deleteAccount: () => Promise<void>;

  // Role checking methods
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  isAdmin: () => boolean;
  isDM: () => boolean;
  isPlayer: () => boolean;

  // Clear error
  clearError: () => void;
}

export const useAuth = (): AuthContextType => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize auth state listener
  useEffect(() => {
    const unsubscribe = AuthService.onAuthStateChanged(
      (userProfile, authError) => {
        if (authError) {
          // Profile fetch failed - set error but don't set loading to false yet
          setError(authError);
          setUser(null);
          // Keep loading true for a moment, then set to false
          setTimeout(() => setLoading(false), 2000);
        } else {
          // Successful auth state change
          setUser(userProfile);
          setError(null);
          setLoading(false);
        }
      }
    );

    return unsubscribe;
  }, []);

  // Sign in with email and password
  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      try {
        setError(null);
        setLoading(true);
        await AuthService.signInWithEmail(email, password);
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Sign in with Google
  const signInWithGoogle = useCallback(
    async (accountType?: "player" | "dm") => {
      try {
        setError(null);
        setLoading(true);

        if (accountType) {
          // Use the new signup method for DM signups
          await AuthService.signUpWithGoogle(accountType);
        } else {
          // Use the regular signin method for existing users
          await AuthService.signInWithGoogle();
        }
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Sign up new user
  const signUp = useCallback(
    async (
      email: string,
      password: string,
      displayName: string,
      accountType: "player" | "dm"
    ) => {
      try {
        setError(null);
        setLoading(true);
        await AuthService.createUserWithEmail(
          email,
          password,
          displayName,
          accountType
        );
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Sign out
  const signOut = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      await AuthService.signOut();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset password
  const resetPassword = useCallback(async (email: string) => {
    try {
      setError(null);
      await AuthService.resetPassword(email);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Email verification methods
  const checkEmailVerification = useCallback(async () => {
    if (!user) {
      throw new Error("No user logged in");
    }
    const verified = await AuthService.checkEmailVerification();
    if (verified) {
      // Reflect verification immediately in local state to avoid redirect loops
      setUser((prev) => (prev ? {...prev, isEmailVerified: true} : prev));
    }
    return verified;
  }, [user]);

  // Force refresh user verification status from Firestore
  const refreshVerificationStatus = useCallback(async () => {
    if (!user) {
      throw new Error("No user logged in");
    }

    try {
      // Get fresh user profile from Firestore
      const userProfile = await AuthService.getUserProfile(user.uid);
      if (userProfile) {
        // Update local state with fresh data
        setUser(userProfile);

        return userProfile.isEmailVerified;
      }
      return false;
    } catch (error) {
      console.error("Error refreshing verification status:", error);
      return false;
    }
  }, [user]);

  const resendEmailVerification = useCallback(async () => {
    if (!user) {
      throw new Error("No user logged in");
    }
    await AuthService.resendEmailVerification();
  }, [user]);

  // Update user profile
  const updateProfile = useCallback(
    async (updates: Partial<UserProfile>) => {
      if (!user) {
        throw new Error("No user logged in");
      }

      try {
        setError(null);
        await AuthService.updateUserProfile(user.uid, updates);
        // Update local state
        setUser((prev) => (prev ? {...prev, ...updates} : null));
      } catch (err: any) {
        setError(err.message);
        throw err;
      }
    },
    [user]
  );

  // Update local user state only (for cases where server is already updated)
  const updateLocalUserState = useCallback((updates: Partial<UserProfile>) => {
    setUser((prev) => (prev ? {...prev, ...updates} : null));
  }, []);

  // Update user settings
  const updateSettings = useCallback(
    async (updates: {
      displayName?: string;
      bio?: string;
      preferences?: {
        emailNotifications: {
          gameReminders: boolean;
          leagueUpdates: boolean;
        };
      };
    }) => {
      if (!user) {
        throw new Error("No user logged in");
      }

      try {
        setError(null);
        await AuthService.updateUserSettings(updates);
        // Update local state
        setUser((prev) => (prev ? {...prev, ...updates} : null));
      } catch (err: any) {
        setError(err.message);
        throw err;
      }
    },
    [user]
  );

  // Delete account
  const deleteAccount = useCallback(async () => {
    if (!user) {
      throw new Error("No user logged in");
    }

    try {
      setError(null);
      await AuthService.deleteAccount();
      // User will be signed out automatically after account deletion
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [user]);

  // Role checking methods
  const hasRole = useCallback(
    (role: UserRole): boolean => {
      return AuthService.hasRole(user, role);
    },
    [user]
  );

  const hasAnyRole = useCallback(
    (roles: UserRole[]): boolean => {
      return AuthService.hasAnyRole(user, roles);
    },
    [user]
  );

  const isAdmin = useCallback((): boolean => {
    return AuthService.isAdmin(user);
  }, [user]);

  const isDM = useCallback((): boolean => {
    return AuthService.isDM(user);
  }, [user]);

  const isPlayer = useCallback((): boolean => {
    return AuthService.isPlayer(user);
  }, [user]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    user,
    loading,
    error,
    isLoggedIn: !!user,
    roles: user?.roles || [],

    // Authentication methods
    signInWithEmail,
    signInWithGoogle,
    signUp,
    signOut,
    resetPassword,

    // Email verification methods
    checkEmailVerification,
    resendEmailVerification,
    refreshVerificationStatus,

    // User profile methods
    updateProfile,
    updateLocalUserState,
    updateSettings,
    deleteAccount,

    // Role checking methods
    hasRole,
    hasAnyRole,
    isAdmin,
    isDM,
    isPlayer,

    // Utility methods
    clearError,
  };
};
